import type uPlot from "uplot";

/** An inclusive x window, in the x scale's own units (unix seconds for a time axis). */
export type ZoomWindow = [number, number];

export interface ZoomPluginOptions {
  /** The user chose a window by clicking its two ends. */
  onZoom: (window: ZoomWindow) => void;
  /** The user cleared the zoom — Escape, or a double-click on the plot. */
  onReset: () => void;
  /** Smallest window worth zooming to, in x units. Guards a stray double-click
   *  being read as a zero-width range. Defaults to two minutes of a time axis. */
  minSpan?: number;
}

/** A drag of fewer than this many pixels is a click, not a selection. */
const DRAG_SLOP_PX = 4;

/**
 * Click two points to zoom the x axis. Escape or double-click resets.
 *
 * UX-006 §5.1 asks for "click two points or drag to zoom; Esc resets". Only the
 * two-click half is implemented, deliberately (Sir, 26 Oct): on a dense intraday
 * curve a drag competes with the cursor readout the chart exists for, and the
 * pointer has to travel the whole window before anything is committed. Two clicks
 * place each edge exactly, with a dashed guide standing at the first while the
 * reader picks the second, and nothing is committed until they do.
 *
 * A pointer that MOVED between down and up is treated as a drag — a scroll, a text
 * selection, a slipped hand — and never counts as the second click.
 *
 * The plugin does not touch `drag.setScale`: every intraday chart pins its x scale
 * with a `range` function, which overrides whatever uPlot re-ranges to, so the
 * window is lifted into React and applied from there instead. One source for the
 * window, which is also what lets the range chip show it.
 */
export function zoomPlugin({ onZoom, onReset, minSpan = 120 }: ZoomPluginOptions): uPlot.Plugin {
  /** First click of a two-click selection, in x units. */
  let anchor: number | null = null;
  /** Where the pointer went down, to tell a click from a drag. */
  let downX: number | null = null;
  let guide: HTMLDivElement | null = null;
  let detach: (() => void) | null = null;

  const showGuide = (u: uPlot, xVal: number) => {
    if (!guide) return;
    guide.style.left = `${u.valToPos(xVal, "x")}px`;
    guide.style.display = "block";
  };
  const hideGuide = () => {
    if (guide) guide.style.display = "none";
  };
  const clearAnchor = () => {
    anchor = null;
    hideGuide();
  };

  const commit = (a: number, b: number) => {
    const [lo, hi] = a <= b ? [a, b] : [b, a];
    if (hi - lo < minSpan) return false;
    onZoom([lo, hi]);
    return true;
  };

  return {
    hooks: {
      init: (u: uPlot) => {
        // The pending-first-click marker. A dashed rule so it reads as "choosing",
        // distinct from the solid cursor line.
        guide = document.createElement("div");
        Object.assign(guide.style, {
          position: "absolute",
          top: "0",
          bottom: "0",
          width: "0",
          borderLeft: "1px dashed var(--sk-chart-selected, var(--sk-text, #111))",
          pointerEvents: "none",
          display: "none",
          zIndex: "9",
        } as CSSStyleDeclaration);
        u.over.appendChild(guide);

        const onDown = (e: MouseEvent) => {
          if (e.button !== 0) return;
          downX = e.offsetX;
        };

        const onUp = (e: MouseEvent) => {
          if (e.button !== 0) return;
          // A pointer that travelled is a drag — a scroll, a text selection, a
          // slipped hand — and must not count as the second click of a selection.
          const moved = downX != null && Math.abs(e.offsetX - downX) > DRAG_SLOP_PX;
          downX = null;
          if (moved) return;
          const xVal = u.posToVal(e.offsetX, "x");
          if (!Number.isFinite(xVal)) return;
          if (anchor == null) {
            anchor = xVal;
            showGuide(u, xVal);
            return;
          }
          const a = anchor;
          clearAnchor();
          commit(a, xVal);
        };

        const onDbl = () => {
          clearAnchor();
          onReset();
        };

        const onKey = (e: KeyboardEvent) => {
          if (e.key !== "Escape") return;
          // Escape abandons a half-made selection first, and only resets the view
          // on a second press — so one key does not undo more than the user meant.
          if (anchor != null) {
            clearAnchor();
            return;
          }
          onReset();
        };

        u.over.addEventListener("mousedown", onDown);
        u.over.addEventListener("mouseup", onUp);
        u.over.addEventListener("dblclick", onDbl);
        document.addEventListener("keydown", onKey);
        detach = () => {
          u.over.removeEventListener("mousedown", onDown);
          u.over.removeEventListener("mouseup", onUp);
          u.over.removeEventListener("dblclick", onDbl);
          document.removeEventListener("keydown", onKey);
        };
      },

      destroy: () => {
        detach?.();
        detach = null;
        guide = null;
        anchor = null;
        downX = null;
      },
    },
  };
}
